import { useState, type FormEvent } from 'react';
import { Button, TextField, TextLink } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { parseRate } from '../create/fields';
import { patchFinancingRate, useInvalidateFinancings } from '../api';
import styles from '../record/record.module.css';

export interface RateFieldProps {
  financingId: string;
  ratePct: number | null;
  onSaved: () => void;
}

function inputOf(pct: number | null): string {
  return pct === null
    ? ''
    : pct.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

/**
 * The assigned financier's rate edit on the „Provision" card (FI17): „Satz ändern" opens the field,
 * „Speichern" sends `PATCH /financings/:id { commissionRatePct }`; the server refuses a frozen commission.
 */
export function RateField({ financingId, ratePct, onSaved }: RateFieldProps) {
  const invalidate = useInvalidateFinancings();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(inputOf(ratePct));
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = parseRate(text);
    if (value === null) {
      setError('Bitte geben Sie einen Satz zwischen 0 und 100 an.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await patchFinancingRate(financingId, value);
      await invalidate(financingId);
      setOpen(false);
      onSaved();
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.fields['commissionRatePct']
          ? cause.fields['commissionRatePct']
          : errorMessage(cause),
      );
    } finally {
      setPending(false);
    }
  }

  if (!open) {
    return (
      <div className={styles.sideActions}>
        <TextLink
          as="button"
          size="small"
          onClick={() => {
            setText(inputOf(ratePct));
            setError(null);
            setOpen(true);
          }}
        >
          Satz ändern
        </TextLink>
      </div>
    );
  }

  return (
    <form className={styles.rateForm} onSubmit={submit} noValidate>
      <TextField
        label="Provisionssatz"
        inputMode="decimal"
        value={text}
        error={error ?? undefined}
        trailing="%"
        autoFocus
        onChange={(event) => setText(event.target.value)}
      />
      <Button type="submit" size="small" loading={pending}>
        Speichern
      </Button>
      <Button variant="secondary" size="small" disabled={pending} onClick={() => setOpen(false)}>
        Abbrechen
      </Button>
    </form>
  );
}
